import React, { useMemo, useState } from 'react';
import { PiKeyLight, PiPlus } from 'react-icons/pi';
import { useAlert } from 'src/contexts/alert-context';
import {
  TCCredential,
  TCCredentialCreateBodyParams,
  TCCredentialUpdateBodyParams,
  TCCredentialUpdateSecretBodyParams,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TokenPagination } from 'src/modules/shared/components/molecules/paginations/token-pagination';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { CredentialPermission } from 'src/modules/shared/types/permissions.type';
import {
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useRotateCredentialSecret,
  useUpdateCredential,
} from 'src/modules/shared/hooks/api/credentials/credentials';
import { CredentialsFilters } from 'src/modules/settings/components/molecules/credentials/credentials-filters';
import { CredentialCreateModal } from 'src/modules/settings/components/organisms/credentials/credential-create-modal';
import { CredentialDeleteModal } from 'src/modules/settings/components/organisms/credentials/credential-delete-modal';
import { CredentialEditModal } from 'src/modules/settings/components/organisms/credentials/credential-edit-modal';
import { CredentialRevealSecretModal } from 'src/modules/settings/components/organisms/credentials/credential-reveal-secret-modal';
import { CredentialRotateSecretModal } from 'src/modules/settings/components/organisms/credentials/credential-rotate-secret-modal';
import { CredentialsTable } from 'src/modules/settings/components/organisms/credentials/credentials-table';
import {
  CREDENTIAL_PROVIDER_OPTIONS,
  DEFAULT_CREDENTIALS_PAGE_SIZE,
  type CredentialKindFilter,
  OTHER_CREDENTIAL_PROVIDER_OPTION,
} from 'src/modules/settings/utils/credentials.util';

/**
 * CredentialsSettingsPage component.
 */
export const CredentialsSettingsPage: React.FC = () => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { user } = useCurrentUser();

  const currentUserId = user?.id ?? null;

  const { hasPermission: canRead } = useHasPermissions(
    CredentialPermission.Read
  );
  const { hasPermission: canCreate } = useHasPermissions(
    CredentialPermission.Create
  );
  const { hasPermission: canUpdate } = useHasPermissions(
    CredentialPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    CredentialPermission.Delete
  );
  const { hasPermission: canManageAll } = useHasPermissions(
    CredentialPermission.Manage
  );
  const { hasPermission: canRevealSecret } = useHasPermissions(
    CredentialPermission.RevealSecret
  );

  const [pageSize, setPageSize] = useState<number>(
    DEFAULT_CREDENTIALS_PAGE_SIZE
  );
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [kindFilter, setKindFilter] = useState<CredentialKindFilter>('all');

  const normalizedProviderFilter = providerFilter.trim();
  const providerQueryParam =
    normalizedProviderFilter.length === 0 ||
    normalizedProviderFilter === OTHER_CREDENTIAL_PROVIDER_OPTION
      ? undefined
      : normalizedProviderFilter;

  const {
    credentials,
    loading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount,
  } = useCredentials({
    limit: pageSize,
    provider: providerQueryParam,
    kind: kindFilter === 'all' ? undefined : kindFilter,
    enabled: canRead,
  });

  const filteredCredentials = useMemo(() => {
    if (normalizedProviderFilter !== OTHER_CREDENTIAL_PROVIDER_OPTION) {
      return credentials;
    }

    const supportedProviders = new Set(
      CREDENTIAL_PROVIDER_OPTIONS.map((option) => option.id)
    );
    return credentials.filter(
      (credential) =>
        !credential.provider || !supportedProviders.has(credential.provider)
    );
  }, [credentials, normalizedProviderFilter]);

  const { createCredential, loading: creating } = useCreateCredential();
  const { updateCredential, loading: updating } = useUpdateCredential();
  const { rotateCredentialSecret, loading: rotating } =
    useRotateCredentialSecret();
  const { deleteCredential, loading: deleting } = useDeleteCredential();

  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [editCredential, setEditCredential] = useState<TCCredential | null>(
    null
  );
  const [rotateCredential, setRotateCredential] = useState<TCCredential | null>(
    null
  );
  const [revealCredential, setRevealCredential] = useState<TCCredential | null>(
    null
  );
  const [deleteCredentialTarget, setDeleteCredentialTarget] =
    useState<TCCredential | null>(null);

  const headerActions = useMemo(() => {
    if (!canCreate) return null;

    return (
      <Button
        onClick={() => setIsCreateOpen(true)}
        variant="primary"
        size="md"
        className="flex items-center gap-2"
      >
        <PiPlus className="w-4 h-4" />
        <Text size="small" className="hidden md:inline">
          {t('settings.credentials.create.button')}
        </Text>
      </Button>
    );
  }, [canCreate, t]);

  const handleLoadMore = () => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  };

  const handleRetry = () => {
    void refetch();
  };

  const handleCreate = async (body: TCCredentialCreateBodyParams) => {
    try {
      await createCredential(body);
      showAlert(t('settings.credentials.alerts.created'), 'success');
      setIsCreateOpen(false);
    } catch (err) {
      showAlert(t('settings.credentials.alerts.error'), 'error');
      throw err;
    }
  };

  const handleEdit = async (params: {
    credentialId: string;
    body: TCCredentialUpdateBodyParams;
  }) => {
    try {
      await updateCredential(params);
      showAlert(t('settings.credentials.alerts.updated'), 'success');
      setEditCredential(null);
    } catch (err) {
      showAlert(t('settings.credentials.alerts.error'), 'error');
      throw err;
    }
  };

  const handleRotate = async (params: {
    credentialId: string;
    body: TCCredentialUpdateSecretBodyParams;
  }) => {
    try {
      await rotateCredentialSecret(params);
      showAlert(t('settings.credentials.alerts.rotated'), 'success');
      setRotateCredential(null);
    } catch (err) {
      showAlert(t('settings.credentials.alerts.error'), 'error');
      throw err;
    }
  };

  const handleDelete = async (credentialId: string) => {
    try {
      await deleteCredential(credentialId);
      showAlert(t('settings.credentials.alerts.deleted'), 'success');
      setDeleteCredentialTarget(null);
    } catch (err) {
      showAlert(t('settings.credentials.alerts.error'), 'error');
      throw err;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Panel className="h-full flex flex-col">
        <PanelHeader className="px-4 py-3 border-b" actions={headerActions}>
          <div className="flex items-center gap-4">
            <PiKeyLight className="w-7 h-7 flex-shrink-0" />
            <Text as="h2" size="h2" weight="medium">
              {t('settings.credentials.title')}
            </Text>
          </div>
        </PanelHeader>

        {canRead ? (
          <CredentialsFilters
            provider={providerFilter}
            onProviderChange={setProviderFilter}
            kind={kindFilter}
            onKindChange={setKindFilter}
            onClear={() => {
              setProviderFilter('');
              setKindFilter('all');
            }}
          />
        ) : null}

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex flex-col gap-4 p-4 pb-8 overflow-y-auto flex-1">
            {!canRead ? (
              <ErrorState
                title={t('settings.credentials.forbidden.title')}
                description={t('settings.credentials.forbidden.description')}
              />
            ) : error ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <ErrorState
                  title={t('settings.credentials.error.title')}
                  description={t('settings.credentials.error.description')}
                  onAction={handleRetry}
                  actionContent={t('settings.credentials.error.retry')}
                />
              </div>
            ) : (
              <>
                <TokenPagination
                  pageSize={pageSize}
                  currentCount={filteredCredentials.length}
                  totalCount={
                    normalizedProviderFilter ===
                    OTHER_CREDENTIAL_PROVIDER_OPTION
                      ? undefined
                      : totalCount
                  }
                  onPageSizeChange={setPageSize}
                  onNextPage={handleLoadMore}
                  hasNextPage={hasNextPage}
                  isLoading={isFetchingNextPage}
                />
                <CredentialsTable
                  credentials={filteredCredentials}
                  loading={loading}
                  isLoadingMore={isFetchingNextPage}
                  currentUserId={currentUserId}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  canManageAll={canManageAll}
                  canRevealSecret={canRevealSecret}
                  onEdit={setEditCredential}
                  onRotateSecret={setRotateCredential}
                  onRevealSecret={setRevealCredential}
                  onDelete={setDeleteCredentialTarget}
                />
              </>
            )}
          </div>
        </div>
      </Panel>

      <CredentialCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
        loading={creating}
      />

      <CredentialEditModal
        credential={editCredential}
        onClose={() => setEditCredential(null)}
        onSubmit={handleEdit}
        loading={updating}
      />

      <CredentialRotateSecretModal
        credential={rotateCredential}
        onClose={() => setRotateCredential(null)}
        onSubmit={handleRotate}
        loading={rotating}
      />

      <CredentialRevealSecretModal
        credential={revealCredential}
        onClose={() => setRevealCredential(null)}
      />

      <CredentialDeleteModal
        credential={deleteCredentialTarget}
        onClose={() => setDeleteCredentialTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
};
